import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PublicRoute from './components/Auth/PublicRoute';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import SetupPassword from './pages/Auth/SetupPassword';
import RegisterOrganization from './pages/Auth/RegisterOrganization';
import RegisterRequerente from './pages/Auth/RegisterRequerente';
import Files from './pages/Files';
import Dashboard from './pages/Dashboard';
import Notifications from './pages/Notifications';
import Support from './pages/Support';
import NotificationTemplates from './pages/Notifications/NotificationTemplates';
import SendNotification from './pages/Notifications/SendNotification';
import Licenses from './pages/Licenses';
import Enterprises from './pages/Enterprises';
import Map from './pages/Map';
import Reports from './pages/Reports';
import ReportCenter from './pages/ReportCenter';
import Users from './pages/Users';
import Settings from './pages/Settings';
import SystemHistory from './pages/Audit/SystemHistory';
import Trash from './pages/Audit/Trash';
import Profile from './pages/Profile';
import SuperAdmin from './pages/SuperAdmin';
import EmailProviders from './pages/admin/EmailProviders';
import EmailTemplates from './pages/admin/EmailTemplates';

// Catalog Service CRUDs
import CompanySizes from './pages/Catalog/CompanySizes';
import CompanyStatuses from './pages/Catalog/CompanyStatuses';
import EconomicActivities from './pages/Catalog/EconomicActivities';

// Contact Service CRUDs
import Contacts from './pages/Contact/Contacts';
import Positions from './pages/Contact/Positions';

// Identity Service CRUDs
import Persons from './pages/Identity/Persons';
import Companies from './pages/Identity/Companies';
import IdentityEnterprises from './pages/Identity/Enterprises';
import Clients from './pages/Identity/Clients';

// Professional Service CRUDs
import TechnicalResponsibles from './pages/Professional/TechnicalResponsibles';
import ProfessionalCouncils from './pages/Professional/ProfessionalCouncils';
import ProfessionalFormations from './pages/Professional/ProfessionalFormations';

// Complaint Service (Denúncias)
import Categories from './pages/Complaint/Categories';
import Complaints from './pages/Complaint/Complaints';

// Institution Service (Órgãos, Setores, Cargos, Servidores)
import Cargos from './pages/Institution/Cargos';
import Orgaos from './pages/Institution/Orgaos';
import Setores from './pages/Institution/Setores';
import Servidores from './pages/Institution/Servidores';

// Process Service (Processos, Tramitações, Tipos de mídia)
import Processos from './pages/Process/Processos';
import ProcessoDetalhe from './pages/Process/ProcessoDetalhe';
import ProcessoInbox from './pages/Process/ProcessoInbox';
import ProcessoRouting from './pages/Process/ProcessoRouting';
import TiposMidia from './pages/Process/TiposMidia';
import LicenseSubmodules from './pages/License/LicenseSubmodules';
import PlanosRegularizacao from './pages/License/PlanosRegularizacao';
import Legislation from './pages/Legislation';
import PortalRequerente from './pages/Portal/PortalRequerente';
import Requerimentos from './pages/Requerimento/Requerimentos';
import Pedidos from './pages/Pedido/Pedidos';
import RequerimentoWizard from './pages/Requerimento/RequerimentoWizard';
import Fiscalizacao from './pages/Fiscalizacao/Fiscalizacao';
import ConsultaProcesso from './pages/Public/ConsultaProcesso';
import DenunciaPublica from './pages/Public/DenunciaPublica';
import ValidarDocumento from './pages/Public/ValidarDocumento';
import MinhasTarefas from './pages/Me/MinhasTarefas';

// License Service (Tipos de licença, Licenças ambientais)
import TiposLicenca from './pages/License/TiposLicenca';
import LicencaAssistidaIA from './pages/License/LicencaAssistidaIA';
import Assistant from './pages/Assistant';
import Condicionantes from './pages/License/Condicionantes';
import { useAuth } from './contexts/AuthContext';
import { useTenant } from './contexts/TenantContext';
import { getAppHomePath } from './constants/navigation';
import { isPlatformOnlySuperAdmin } from './utils/platformRoles';
import CarteiraClientes from './pages/Carteira/CarteiraClientes';

function HomeRedirect() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  if (isPlatformOnlySuperAdmin(user)) {
    return <Navigate to={getAppHomePath(user, tenant)} replace />;
  }
  return <Dashboard />;
}

function LicenseLegacyListRedirect() {
  const { state } = useLocation();
  return <Navigate to="/licenses" replace state={state} />;
}

function RedirectLegacyId({ toPrefix }) {
  const { id } = useParams();
  return <Navigate to={`${toPrefix}/${id}`} replace />;
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
      <TenantProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Rotas de Autenticação */}
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              <Route path="/register" element={<Navigate to="/register-organization" replace />} />
              <Route path="/onboarding" element={<Navigate to="/register-organization" replace />} />
              <Route path="/register-organization" element={
                <PublicRoute>
                  <RegisterOrganization />
                </PublicRoute>
              } />
              <Route path="/primeiro-acesso" element={
                <PublicRoute>
                  <RegisterRequerente />
                </PublicRoute>
              } />
              <Route path="/forgot-password" element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } />
              <Route path="/reset-password" element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              } />
              <Route path="/setup-password" element={
                <PublicRoute>
                  <SetupPassword />
                </PublicRoute>
              } />
              <Route path="/process-lookup" element={<ConsultaProcesso />} />
              <Route path="/public-complaint" element={<DenunciaPublica />} />
              <Route path="/validar" element={<ValidarDocumento />} />
              <Route path="/validar/:codigo" element={<ValidarDocumento />} />
              <Route path="/consulta-processo" element={<Navigate to="/process-lookup" replace />} />
              <Route path="/denuncia" element={<Navigate to="/public-complaint" replace />} />
              
              {/* Rotas Protegidas */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<HomeRedirect />} />
                <Route path="me" element={<MinhasTarefas />} />
                <Route path="assistant" element={<Assistant />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="support" element={<Support />} />
                <Route path="notifications/templates" element={<NotificationTemplates />} />
                <Route path="notifications/send" element={<SendNotification />} />
                <Route path="licenses" element={<Licenses />} />
                <Route path="licenses/assisted" element={<LicencaAssistidaIA />} />
                <Route path="portfolio" element={<CarteiraClientes />} />
                <Route path="carteira" element={<Navigate to="/portfolio" replace />} />
                <Route path="enterprises" element={<Enterprises />} />
                <Route path="map" element={<Map />} />
                <Route path="reports" element={<ReportCenter />} />
                <Route path="reports/legacy" element={<Reports />} />
                <Route path="users" element={<Users />} />
                <Route path="history" element={<SystemHistory />} />
                <Route path="trash" element={<Trash />} />
                <Route path="settings" element={<Settings />} />
                <Route path="super-admin" element={<SuperAdmin />} />
                <Route path="admin/email-providers" element={<EmailProviders />} />
                <Route path="admin/email-templates" element={<EmailTemplates />} />
                <Route path="profile" element={<Profile />} />
                
                {/* Catalog Service Routes */}
                <Route path="catalog/company-sizes" element={<CompanySizes />} />
                <Route path="catalog/company-statuses" element={<CompanyStatuses />} />
                <Route path="catalog/economic-activities" element={<EconomicActivities />} />
                
                {/* Contact Service Routes */}
                <Route path="contact/contacts" element={<Contacts />} />
                <Route path="contact/positions" element={<Positions />} />
                
                {/* Identity Service Routes */}
                <Route path="identity/persons" element={<Persons />} />
                <Route path="identity/companies" element={<Companies />} />
                <Route path="identity/enterprises" element={<IdentityEnterprises />} />
                <Route path="identity/clients" element={<Clients />} />
                
                {/* Professional Service Routes */}
                <Route path="professional/technical-responsibles" element={<TechnicalResponsibles />} />
                <Route path="professional/professional-councils" element={<ProfessionalCouncils />} />
                <Route path="professional/professional-formations" element={<ProfessionalFormations />} />

                {/* Complaint Service (Denúncias) */}
                <Route path="complaint/categories" element={<Categories />} />
                <Route path="complaint/complaints" element={<Complaints />} />

                {/* Institution Service */}
                <Route path="institution/job-titles" element={<Cargos />} />
                <Route path="institution/agencies" element={<Orgaos />} />
                <Route path="institution/departments" element={<Setores />} />
                <Route path="institution/staff" element={<Servidores />} />
                <Route path="institution/cargos" element={<Navigate to="/institution/job-titles" replace />} />
                <Route path="institution/orgaos" element={<Navigate to="/institution/agencies" replace />} />
                <Route path="institution/setores" element={<Navigate to="/institution/departments" replace />} />
                <Route path="institution/servidores" element={<Navigate to="/institution/staff" replace />} />

                {/* Process Service */}
                <Route path="process/cases" element={<Processos />} />
                <Route path="process/cases/:id" element={<ProcessoDetalhe />} />
                <Route path="process/routing" element={<ProcessoRouting />} />
                <Route path="process/inbox" element={<ProcessoInbox />} />
                <Route path="process/media-types" element={<TiposMidia />} />
                <Route path="process/processos" element={<Navigate to="/process/cases" replace />} />
                <Route path="process/processos/:id" element={<RedirectLegacyId toPrefix="/process/cases" />} />
                <Route path="process/tipos-midia" element={<Navigate to="/process/media-types" replace />} />

                {/* License Service */}
                <Route path="license/types" element={<TiposLicenca />} />
                <Route path="license/conditions" element={<Condicionantes />} />
                <Route path="license/submodules" element={<LicenseSubmodules />} />
                <Route path="license/regularization-plans" element={<PlanosRegularizacao />} />
                <Route path="license/tipos-licenca" element={<Navigate to="/license/types" replace />} />
                <Route path="license/condicionantes" element={<Navigate to="/license/conditions" replace />} />
                <Route path="license/licencas" element={<LicenseLegacyListRedirect />} />
                <Route path="license/licencas/assistida" element={<Navigate to="/licenses/assisted" replace />} />
                <Route path="license/submodulos" element={<Navigate to="/license/submodules" replace />} />
                <Route path="license/planos-regularizacao" element={<Navigate to="/license/regularization-plans" replace />} />
                <Route path="legislation" element={<Legislation />} />
                <Route path="portal" element={<PortalRequerente />} />
                <Route path="requerimentos" element={<Requerimentos />} />
                <Route path="requerimentos/novo" element={<RequerimentoWizard />} />
                <Route path="requerimentos/:id" element={<RequerimentoWizard />} />
                <Route path="pedidos" element={<Pedidos />} />
                <Route path="files" element={<Files />} />
                <Route path="inspections" element={<Fiscalizacao />} />
                <Route path="fiscalizacao" element={<Navigate to="/inspections" replace />} />
              </Route>
              </Routes>
            
            {/* Toast Notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                className: '!bg-white !text-gray-900 !shadow-strong !border !border-gray-200 !rounded-lg',
                style: {},
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#2E7D32',
                    secondary: '#fff',
                  },
                  className:
                    '!bg-white !text-gray-900 !shadow-strong !border !border-success-200 !rounded-lg',
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#D32F2F',
                    secondary: '#fff',
                  },
                  className:
                    '!bg-white !text-gray-900 !shadow-strong !border !border-danger-200 !rounded-lg',
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
      </TenantProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
